import React, { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, HandCoins, PiggyBank, ChartLineUp, FilePdf, FileXls, SignIn, PaperPlaneTilt, Gift, Clock, CalendarBlank } from "@phosphor-icons/react";
import { money } from "../lib/format";
import { api } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { es } from "date-fns/locale";
import { toast } from "sonner";

function toYMD(d) {
  if (!d) return "";
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function DateField({ label, testid, value, onChange }) {
  const [open, setOpen] = useState(false);
  const dateVal = value ? new Date(value + "T00:00:00") : undefined;
  const display = dateVal ? dateVal.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" }) : label;
  return (
    <div>
      <label className="text-[11px] text-slate-400 block mb-1">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" data-testid={testid} className="h-9 justify-start gap-2 font-normal text-slate-600 min-w-[150px]">
            <CalendarBlank size={15} /> {display}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={es} selected={dateVal} onSelect={(d) => { onChange(toYMD(d)); setOpen(false); }} initialFocus />
          {value ? (
            <div className="p-2 border-t border-gray-100">
              <Button size="sm" variant="ghost" className="w-full text-slate-500" onClick={() => { onChange(""); setOpen(false); }}>Limpiar</Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const META = {
  deposit: { icon: ArrowDown, cls: "bg-emerald-100 text-emerald-700", sign: "+" },
  withdrawal: { icon: ArrowUp, cls: "bg-red-100 text-red-600", sign: "−" },
  loan_payment: { icon: HandCoins, cls: "bg-amber-100 text-amber-700", sign: "" },
  transfer_in: { icon: PaperPlaneTilt, cls: "bg-emerald-100 text-emerald-700", sign: "+" },
  transfer_out: { icon: PaperPlaneTilt, cls: "bg-red-100 text-red-600", sign: "−" },
  referral: { icon: Gift, cls: "bg-teal-100 text-teal-700", sign: "+" },
  account_open: { icon: PiggyBank, cls: "bg-emerald-100 text-emerald-700", sign: "" },
  loan_open: { icon: ChartLineUp, cls: "bg-amber-100 text-amber-700", sign: "" },
  certificate: { icon: FilePdf, cls: "bg-slate-100 text-slate-600", sign: "" },
  login: { icon: SignIn, cls: "bg-blue-100 text-blue-700", sign: "" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return (iso || "").slice(0, 16).replace("T", " "); }
};

export function ActivityFeed({ items, loading, emptyText = "Aún no hay actividades registradas." }) {
  if (loading) return <p className="text-slate-500 py-6">Cargando…</p>;
  if (!items || items.length === 0) {
    return <p className="text-slate-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="no-activities">{emptyText}</p>;
  }
  return (
    <div className="space-y-2" data-testid="activity-feed">
      {items.map((a, i) => {
        const m = META[a.kind] || { icon: Clock, cls: "bg-slate-100 text-slate-500", sign: "" };
        const Icon = m.icon;
        return (
          <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3" data-testid={`activity-item-${i}`}>
            <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${m.cls}`}>
              <Icon size={18} weight="bold" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-emerald-900 text-sm leading-tight">{a.title}</p>
              {a.detail ? <p className="text-xs text-slate-400 truncate">{a.detail}</p> : null}
            </div>
            <div className="text-right shrink-0">
              {a.amount != null && a.currency ? (
                <p className="font-mono-num font-bold text-sm text-emerald-900">{m.sign}{money(a.amount, a.currency)}</p>
              ) : null}
              <p className="text-[11px] text-slate-400">{fmtDate(a.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CATS = [["", "Todas"], ["deposits", "Depósitos"], ["withdrawals", "Retiros"], ["payments", "Abonos"], ["accounts", "Cuentas"], ["certificates", "Certificados"], ["logins", "Sesiones"]];

export function ActivityPanel({ baseUrl, emptyText }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const buildParams = (extra) => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  };

  useEffect(() => {
    setLoading(true);
    const q = buildParams();
    api.get(`${baseUrl}${q ? `?${q}` : ""}`).then((r) => setItems(r.data)).catch(() => { setItems([]); toast.error("No se pudieron cargar las actividades"); }).finally(() => setLoading(false));
  }, [category, start, end]); // eslint-disable-line

  const exportActs = async (fmt) => {
    try {
      const q = buildParams({ format: fmt });
      const res = await api.get(`${baseUrl}/export?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `actividades.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div className="space-y-3" data-testid="activity-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[11px] text-slate-400 block mb-1">Tipo</label>
          <select data-testid="act-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <DateField label="Desde" testid="act-start" value={start} onChange={setStart} />
        <DateField label="Hasta" testid="act-end" value={end} onChange={setEnd} />
        <div className="flex gap-2 ml-auto">
          <Button data-testid="act-export-excel" onClick={() => exportActs("excel")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><FileXls size={15} weight="bold" /> Excel</Button>
          <Button data-testid="act-export-pdf" onClick={() => exportActs("pdf")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><FilePdf size={15} weight="bold" /> PDF</Button>
        </div>
      </div>
      <ActivityFeed items={items} loading={loading} emptyText={emptyText} />
    </div>
  );
}
